import { inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { IDictionary } from '@core/common.model';
import { LoggerService } from '@core/logger/logger.service';
import { ProductHelperService } from '@core/product/product-helper.service';
import { IVenueConfig } from '@core/venue-config/models/venue-config.model';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { IMatomoAbEnterExperiment, IMatomoAbExperiment } from './matomo-abtesting.model';
import { TMatomoAbEvent, TMatomoAbTest } from './matomo.const';
import { IAnalyticsWindow } from './analytics_item.model';
@Injectable({
  providedIn: 'root'
})
export class MatomoAbTestingService {
  private document = inject(DOCUMENT);
  private zone = inject(NgZone);
  private loggerService = inject(LoggerService);
  private productHelperService = inject(ProductHelperService);
  private venueConfigService = inject(VenueConfigService);

  private venueConfig?: IVenueConfig;
  private window: IAnalyticsWindow = this.document.defaultView as Window as IAnalyticsWindow;
  private experiments: IMatomoAbExperiment[] = [];
  private variations: Record<string, string> = {};
  private eligibleExperiments: TMatomoAbTest[] = [];
  private isInitialized$ = new BehaviorSubject<boolean>(false);

  getIsInitialized(): Observable<boolean> {
    return this.isInitialized$.asObservable();
  }

  setIsInitialized(value: boolean): void {
    this.isInitialized$.next(value);
  }

  bootstrap(venueConfig: IVenueConfig, venueSlug: string): void {
    try {
      if (!this.window?.Matomo?.AbTesting.Experiment) {
        this.loggerService.warn('Unable to initialize Matomo Ab testing - window.Matomo.AbTesting not available');
        return;
      }

      this.venueConfig = venueConfig;
      this.setMatomoExperiments();
      this.setEligibleExperiments(venueSlug);

      if (!this.experiments.length || !this.eligibleExperiments.length) {
        return;
      }

      this.enterExperiments();
    } catch (error: unknown) {
      this.loggerService.warn(`Unable to initialize Matomo Ab testing - ${error}`);
    } finally {
      this.setIsInitialized(true);
    }
  }

  enterExperiments(): void {
    this.eligibleExperiments.forEach((experimentName) => {
      this.enterExperiment(experimentName as TMatomoAbTest);
    });
  }

  enterExperiment(experimentName: TMatomoAbTest): void {
    const experiment = this.experiments.find((experiment) => experiment.name === experimentName);
    if (!experiment) return;

    this.zone.runOutsideAngular(() => {
      try {
        const Experiment = this.window.Matomo?.AbTesting.Experiment;
        if (!Experiment) {
          this.loggerService.warn(
            `Unable to enter visitor into Experiment (${experimentName}) as Matomo AB testing object is not available`
          );
          return;
        }

        const entered = new Experiment(experiment);
        const variation = entered.getActivatedVariationName() as string;
        this.variations[experiment.name] = variation;

        const enterExperiment: IMatomoAbEnterExperiment = {
          experiment: experiment.name,
          variation: variation
        };

        this.callMatomoAb('AbTesting::enter', enterExperiment);
      } catch (error: unknown) {
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        this.loggerService.warn(`Unable to push data to Matomo Analytics AB Test '${experiment.name}'`, {
          message: errorMessage
        });
      }
    });
  }

  callMatomoAb(event: TMatomoAbEvent, properties: IMatomoAbEnterExperiment): Promise<void> | void {
    const payload: (string | number | boolean | IDictionary<string> | IMatomoAbEnterExperiment)[] = [event];
    payload.push(properties);

    return this.window._paq.push(payload);
  }

  private setEligibleExperiments(venueSlug: string): void {
    const experiments = this.getExperimentsByVenue();

    this.eligibleExperiments = (Object.keys(experiments) as TMatomoAbTest[]).filter((experimentName) => {
      const experiment = experiments[experimentName];
      return experiment.includes('*') || experiment.includes(venueSlug);
    });
  }

  private getExperimentsByVenue(): Record<TMatomoAbTest, string[]> {
    return {
      AABaseline: ['*']
      // ShowSoldOutSessionTimes: ['*'],
      // ShowGuestCountSelector: ['*']
    };
  }

  private setMatomoExperiments() {
    const defaultActivate = () => {};

    const experiments = [
      // {
      //   name: 'ShowGuestCountSelector',
      //   percentage: 100,
      //   variations: [
      //     {
      //       name: 'original',
      //       activate: defaultActivate
      //     },
      //     {
      //       name: 'variation1',
      //       percentage: 50,
      //       activate: () => this.venueConfigService.setShowGuestCountSelector(true)
      //     }
      //   ],
      //   trigger: () => {
      //     // Enter experiment if checkout:
      //     // 1. has guest count disabled
      //     // 2. is not a food and beverage checkout
      //     // 3. contains at least one relevant product to filter
      //     return (
      //       !this.venueConfigService.isGuestCountEnabled() &&
      //       !this.venueConfigService.isFoodAndBeverageCheckout() &&
      //       this.productHelperService.checkoutContainsGuestCountProducts()
      //     );
      //   }
      // },
      // {
      //   name: 'ShowSoldOutSessionTimes',
      //   percentage: 100,
      //   variations: [
      //     {
      //       name: 'original',
      //       activate: defaultActivate
      //     },
      //     {
      //       name: 'variation1',
      //       percentage: 50,
      //       activate: () => this.venueConfigService.setShowSoldOutSessionTimes(true)
      //     }
      //   ],
      //   trigger: () => {
      //     // Enter experiment if showSoldOutSessionTimes is disabled
      //     return !this.venueConfig!.productsPage.showSoldOutSessionTimes;
      //   }
      // },
      {
        name: 'AABaseline',
        percentage: 100,
        variations: [
          {
            name: 'original',
            activate: defaultActivate
          },
          {
            name: 'variation1',
            percentage: 50,
            activate: defaultActivate
          }
        ],
        trigger: () => true
      }
    ] satisfies IMatomoAbExperiment[];

    this.experiments = experiments.filter((experiment) => experiment.trigger());
  }
}
